import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteChangeReason, AutocompleteInputChangeReason, Tooltip } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import InfiniteAutocomplete, { InfiniteAutocompleteProps } from './InfiniteAutocomplete';
import {
    AxiosInfinitePageableResponse,
    PAGEABLE_SIZE,
    PageableRequest,
    PageableResponse,
} from '../types/AxiosPageableResponse';
import Utils from './Utils';
import { OemProgram } from '../types/DspEntity';
import { OemProgramCriteria } from '../types/Criteria/OemProgramCriteria';

const getOptionKey = (option: OemProgram | any): string | number => {
    return option?.id as number;
};

const getOptionLabel = (option: OemProgram | any): string => {
    return option?.oemProgram || '';
};

const defaultPageable: PageableRequest = {
    page: 1,
    size: PAGEABLE_SIZE,
    sort: 'asc',
    sortBy: 'oemProgram',
};

const fetch = async (
    criteria: OemProgramCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<PageableResponse<OemProgram>> => {
    const source = axios.CancelToken.source();

    if (signal) {
        if (signal.aborted) source.cancel('aborted');
        signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
    }

    const response = await axios.get('/api/oem-program/filter', {
        headers: { XBusy: 'false' },
        params: {
            ...criteria,
            ...pageable,
            page: page - 1,
            oemIds: criteria?.oemIds?.join(',') || undefined,
            ids: criteria?.ids?.join(',') || undefined,
            agencyIds: criteria?.agencyIds?.join(',') || undefined,
            advertiserIds: criteria?.advertiserIds?.join(',') || undefined,
            agencyMarketRegions: criteria?.agencyMarketRegions?.join(',') || undefined,
        },
        cancelToken: source.token,
    });

    return response.data;
};

const fetchInitial = async (
    ids: number[] | number | null | undefined,
    criteria: OemProgramCriteria,
    signal?: AbortSignal
): Promise<any> => {
    const _ids: number[] = Array.isArray(ids) ? ids : ids != null ? [ids] : [];

    if (_ids.length === 0) {
        return [];
    }

    const { content } = await fetch({ ...criteria, ids: _ids }, { page: 1, size: 1000 }, signal);

    return content;
};

const fetchInfinite = async (
    criteria: OemProgramCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<AxiosInfinitePageableResponse<OemProgram>> => {
    const { content } = await fetch(criteria, { page, ...pageable }, signal);

    return {
        items: content,
        nextPage: content.length === pageable.size ? page + 1 : undefined,
    };
};

const fetchMass = async (criteria: OemProgramCriteria, signal?: AbortSignal): Promise<any> => {
    const { content } = await fetch(criteria, { page: 1, size: 1000 }, signal);

    return content;
};

interface OemProgramCheckboxAutoCompleteProps<
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
> extends Omit<
        InfiniteAutocompleteProps<OemProgram, Multiple, DisableClearable, FreeSolo>,
        'options' | 'value' | 'getOptionKey' | 'getOptionLabel'
    > {
    onLoading?: () => void;
    criteria?: OemProgramCriteria;
    value: Multiple extends true ? number[] : number | null;
    deferChangeUntilClose?: boolean;
}

const OemProgramCheckboxAutoComplete = <
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>({
    onLoading = () => {},
    onChange,
    disabled = false,
    deferChangeUntilClose = false,
    ...props
}: OemProgramCheckboxAutoCompleteProps<Multiple, DisableClearable, FreeSolo>) => {
    const { debounce } = Utils;

    const [options, setOptions] = useState<OemProgram[]>([]);
    const [value, setValue] = useState<
        InfiniteAutocompleteProps<OemProgram, Multiple, DisableClearable, FreeSolo>['value']
    >(
        (props.multiple ? [] : null) as InfiniteAutocompleteProps<
            OemProgram,
            Multiple,
            DisableClearable,
            FreeSolo
        >['value']
    );
    const [inputValue, setInputValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isMassSelected, setIsMassSelected] = useState<boolean>(false);
    const pendingValueRef = useRef<
        InfiniteAutocompleteProps<OemProgram, Multiple, DisableClearable, FreeSolo>['value'] | null
    >(null);
    const hasChangedWhileOpenRef = useRef<boolean>(false);

    const criteriaWithoutName = useMemo((): OemProgramCriteria => {
        return {
            ids: props?.criteria?.ids || [],
            oemIds: props?.criteria?.oemIds || [],
            agencyIds: props?.criteria?.agencyIds || [],
            advertiserIds: props?.criteria?.advertiserIds || [],
            agencyMarketRegions: props?.criteria?.agencyMarketRegions || [],
        };
    }, [props.criteria]);

    const criteria = useMemo((): OemProgramCriteria => {
        return {
            ...criteriaWithoutName,
            name: inputValue,
        };
    }, [criteriaWithoutName, inputValue]);

    // Fetch initial value if IDs are provided
    const { data: initialValue, isLoading: isLoadingInitialValue } = useQuery({
        queryKey: ['infinite/oemPrograms/initial', props.value, criteriaWithoutName],
        queryFn: ({ signal }) => fetchInitial(props.value, criteria, signal),
        enabled: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (initialValue) {
            if (props.multiple) {
                setValue((initialValue ?? []) as any);
            } else {
                setValue((initialValue[0] ?? null) as any);
            }
        }
    }, [initialValue, props.multiple]);

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } = useInfiniteQuery({
        queryKey: ['infinite/oemPrograms', criteria],
        queryFn: ({ pageParam, signal }) =>
            fetchInfinite(criteria, { page: pageParam || 1, size: defaultPageable.size }, signal),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 0,
        // staleTime: 1000 * 60 * 5,
        enabled: true,
        refetchOnWindowFocus: false,
    });

    const { refetch: refetchMass, isLoading: isLoadingMass } = useQuery({
        queryKey: ['infinite/oemPrograms/mass', criteria],
        queryFn: ({ signal }) => fetchMass(criteria, signal),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const handleOnClose = useCallback((): void => {
        setIsOpen(false);
        if (deferChangeUntilClose && hasChangedWhileOpenRef.current && pendingValueRef.current !== null) {
            onChange(pendingValueRef.current as any);
        }
        hasChangedWhileOpenRef.current = false;
        pendingValueRef.current = null;
    }, [deferChangeUntilClose, onChange]);

    const handleOnChange = useCallback(
        (
            newValue: InfiniteAutocompleteProps<OemProgram, Multiple, DisableClearable, FreeSolo>['value'] | undefined,
            reason?: AutocompleteChangeReason
        ): void => {
            setValue(newValue);
            if (deferChangeUntilClose) {
                pendingValueRef.current = newValue ?? null;
                hasChangedWhileOpenRef.current = true;
            } else {
                onChange(newValue as any);
            }

            // If cleared via X button, trigger close logic immediately
            if (reason === 'clear') {
                handleOnClose();
            }
        },
        [deferChangeUntilClose, handleOnClose, onChange]
    );

    const debouncedOnInputChange = useRef(
        debounce((): void => {
            refetch();
        }, 1000)
    ).current;

    const isLoading = useMemo((): boolean => {
        return isFetching || isFetchingNextPage || isLoadingInitialValue || isLoadingMass;
    }, [isFetching, isFetchingNextPage, isLoadingInitialValue, isLoadingMass]);

    const isSearching = inputValue.trim().length > 0;
    const isDisabled = disabled || (!isLoading && options.length === 0 && !isSearching);

    const handleOnInputChange = (
        event: React.SyntheticEvent,
        newInputValue: string,
        reason: AutocompleteInputChangeReason
    ): void => {
        setInputValue(newInputValue);
        debouncedOnInputChange(newInputValue);
    };

    const handleOnScroll = useCallback((): void => {
        if (!isMassSelected) {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isMassSelected]);

    useEffect((): void => {
        setOptions(data?.pages.flatMap((page) => page.items) || []);
    }, [data]);

    useEffect((): void => {
        if (isLoading) {
            onLoading();
        }
    }, [isLoading, onLoading]);

    useEffect(() => {
        if (!isOpen) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        setIsMassSelected(false);
    }, [criteria]);

    const handleOnFetchMass = async (): Promise<ReadonlyArray<OemProgram>> => {
        const { data } = await refetchMass();

        setIsMassSelected(true);

        return data;
    };

    return (
        <Tooltip
            arrow
            disableHoverListener={!isDisabled}
            title="The OEM program filter is disabled because there are no OEM programs matching the current filter selections."
        >
            <InfiniteAutocomplete
                disableCloseOnSelect={props.multiple}
                openOnFocus={props.multiple}
                selectOnFocus={props.multiple}
                blurOnSelect={false}
                clearOnBlur={false}
                label="OEM Program"
                {...props}
                disabled={isDisabled}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                options={options}
                value={value}
                loading={isLoading}
                onChange={handleOnChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                onScroll={handleOnScroll}
                onFetchMass={handleOnFetchMass}
                onOpen={(): void => setIsOpen(true)}
                onClose={handleOnClose}
            />
        </Tooltip>
    );
};

export type { OemProgramCheckboxAutoCompleteProps };
export default OemProgramCheckboxAutoComplete;
